import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ThemeProvider } from "./context/ThemeContext";
import { LanguageProvider } from "./context/LanguageContext";
import authService from "./services/authService";
import useUserStore from "./stores/userStore";

// Import i18n configuration
import "./i18n/config/i18n";

import Header from "./components/layout/Header";
import EmailVerifiedRoute from "./components/layout/EmailVerifiedRoute";
import routes from "./config/routes";
import useChatStore from "./stores/chatStore";

// Loading component for lazy-loaded routes
const PageLoading = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600 dark:text-gray-300">Loading...</p>
    </div>
  </div>
);

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const isAuthenticated =
    useUserStore((state) => state.isAuthenticated) ||
    authService.isAuthenticated();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route component (redirects to home if already authenticated)
const PublicRoute = ({ children }) => {
  const isAuthenticated =
    useUserStore((state) => state.isAuthenticated) ||
    authService.isAuthenticated();

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const App = () => {
  const { t, i18n } = useTranslation();
  const chatStore = useChatStore();
  const handleClearChat = () => {
    chatStore.clearChatHistory();
  };

  useEffect(() => {
    // Initialize theme and language on app load
    const savedTheme = localStorage.getItem("theme") || "system";
    const savedLanguage = localStorage.getItem("language") || "en";

    // Apply theme
    if (savedTheme === "dark") {
      document.documentElement.classList.add("dark");
    } else if (savedTheme === "light") {
      document.documentElement.classList.remove("dark");
    }

    // Apply language direction and initialize i18n
    if (savedLanguage === "ar") {
      document.documentElement.dir = "rtl";
      document.documentElement.lang = "ar";
      i18n.changeLanguage("ar");
    } else {
      document.documentElement.dir = "ltr";
      document.documentElement.lang = savedLanguage;
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  return (
    <ThemeProvider>
      <LanguageProvider>
        <Router>
          <Routes>
            {routes.map((route, idx) => {
              let element = <route.element />;
              // Wrap with appropriate guards
              if (
                route.public === true &&
                ["/login", "/signup", "/verify-email", "/verify-otp"].includes(
                  route.path
                )
              ) {
                element = <PublicRoute>{element}</PublicRoute>;
              } else {
                // All other routes are protected and need Header
                element = (
                  <ProtectedRoute>
                    <>
                      <Header
                        messages={chatStore.messages}
                        onClearChat={handleClearChat}
                      />
                      <EmailVerifiedRoute>{element}</EmailVerifiedRoute>
                    </>
                  </ProtectedRoute>
                );
              }
              return (
                <Route key={route.path} path={route.path} element={element} />
              );
            })}
          </Routes>
        </Router>
      </LanguageProvider>
    </ThemeProvider>
  );
};

export default App;
